import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  BellRing as BellRingIcon,
  ChevronDown as CaretDownIcon,
  ChevronLeft as CaretLeftIcon,
  ChevronRight as CaretRightIcon,
  ChevronsLeft as CaretFirstIcon,
  ChevronsRight as CaretLastIcon,
  RefreshCw as RefreshIcon,
  Search as SearchIcon
} from 'lucide-react'
import {
  PLANT_ALARM_PRIORITIES,
  useGetPlantActiveAlarmsQuery,
  useGetPlantAlarmsQuery,
  usePlantSessionPaint,
  type PlantActiveAlarm,
  type PlantActiveAlarmsGateway,
  type PlantAlarm,
  type PlantAlarmAckFilter,
  type PlantAlarmDefinitionGroup,
  type PlantAlarmInstanceGroup,
  type PlantAlarmLiveState,
  type PlantAlarmPriority,
  type PlantAlarmStatusFilter,
  type PlantAlarmsIndex,
  type PlantSessionMarkView
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import { Switch } from '../../components/ui/switch'
import { useChatAppContext } from '../../chat-app-context'
import { buildAlarmsAppContextEntries, type ExpandedAlarm } from './plant-app-context'
import { Empty } from '../../components/primitives/Empty'
import { TIER_PILL_CLASSES, type SeverityTier } from '../../components/primitives/severityTier'
import {
  SessionDescendantPaintMarks,
  SessionGhostRow,
  SessionRowEndMark,
  collectGhostsForContainers,
  collectOrphanLabeledGhosts,
  sessionMarkClass,
  type PlantSessionGhostView
} from './session-mark-ui'

/**
 * `/plant/alarms` — Alarms tab landing.
 *
 * Reads every `:Alarm` node emitted at ingest by `mapTagAlarms`. Two
 * display modes — selectable from the page header:
 *
 *   - **Grouped** (default): a collapsible tree
 *     `UDTDefinition > UDTInstance > full tag path`. Standalone-tag
 *     alarms (no inbound `HAS_ALARM_DEFINITION` edge) fall into an
 *     "Ungrouped / No UDT" section, matching the strays pattern from
 *     other Plant View tabs.
 *   - **Flat**: a single searchable table with one row per alarm.
 *
 * Priority filtering (Diagnostic / Low / Medium / High / Critical) is a
 * server-side allow-list so the wire payload shrinks when the user
 * narrows the scope; the chips toggle the active set.
 *
 * Session paint marks alarm leaves/rows; definition/instance chrome
 * shows a touched dot when any descendant alarm is painted; deleted
 * alarms ghost under their instance (via HAS_ALARM → Tag) or Ungrouped.
 */
/**
 * Accordion expansion lives on the page, not in each section, so the chat
 * app-context producer can name the alarm rows the user is looking at.
 * Keys are namespaced because a definition and an instance can share an id.
 */
const UNGROUPED_SECTION_KEY = 'ungrouped'
const definitionSectionKey = (id: string): string => `definition:${id}`
const instanceSectionKey = (id: string): string => `instance:${id}`

const AlarmsPage = (): React.ReactElement => {
  const [grouped, setGrouped] = React.useState(true)
  const [expandedKeys, setExpandedKeys] = React.useState<ReadonlySet<string>>(() => new Set())
  const toggleExpanded = React.useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  const [search, setSearch] = React.useState('')
  // `null` (no entries selected) → no filter, every priority returned.
  // Mirrors the "absent param = no filter" contract of `getPlantAlarms`.
  const [activePriorities, setActivePriorities] = React.useState<Set<PlantAlarmPriority>>(
    () => new Set()
  )
  // Tri-state live-status filter. `all` lists every configured alarm
  // regardless of live state (the default — useful for browsing the
  // catalog); `active` narrows to alarms the gateway currently reports
  // as `Active*`, with `ackFilter` further subdividing into
  // acked/unacked.
  const [statusFilter, setStatusFilter] = React.useState<PlantAlarmStatusFilter>('all')
  const [ackFilter, setAckFilter] = React.useState<PlantAlarmAckFilter>('all')
  // Shared page state across both view modes. Reset to 0 whenever the
  // user changes the view, the priority allow-list, the flat-view
  // search query, or any of the live-status filters so they don't land
  // on an empty page after the data set shrinks under them.
  const [page, setPage] = React.useState(0)

  const queryArg = React.useMemo(
    () => (activePriorities.size > 0 ? { priorities: Array.from(activePriorities) } : undefined),
    [activePriorities]
  )

  const { data, isLoading, isError, isFetching } = useGetPlantAlarmsQuery(queryArg)
  const index: PlantAlarmsIndex = data ?? EMPTY_INDEX
  const totalCount = index.alarms.length
  const { active, filter, ghostsByContainerId, markFor } = usePlantSessionPaint()
  const paintOn = active && filter.enabled
  const liveTagIds = React.useMemo(
    () => new Set(index.alarms.map((alarm) => alarm.tag_id)),
    [index.alarms]
  )
  const orphanAlarmGhosts = React.useMemo((): PlantSessionGhostView[] => {
    if (!paintOn) return []
    return collectOrphanLabeledGhosts(liveTagIds, ghostsByContainerId, markFor, 'Alarm')
  }, [ghostsByContainerId, liveTagIds, markFor, paintOn])

  // Ghost-only sessions (every live alarm deleted) must not fall through
  // to the empty shell — same pattern as Compute `showEmpty` / Areas strays.
  const showEmpty = !isError && !isLoading && totalCount === 0 && orphanAlarmGhosts.length === 0
  // Flat view has no ghost rows; host orphan deletes under Ungrouped instead.
  const showGrouped = grouped || (totalCount === 0 && orphanAlarmGhosts.length > 0)

  // Derive the unique (gateway_url, project) set from the configured
  // alarms so the live-status fan-out only hits each gateway once.
  // Mount-time fetch is automatic; the user can also trigger a manual
  // refresh via `refetchLive` below. We `skip` while no alarms have a
  // resolved gateway so empty plants don't fire a no-op request.
  const liveGateways = React.useMemo(() => collectGateways(index.alarms), [index.alarms])
  const {
    data: liveData,
    isFetching: liveFetching,
    isError: liveIsError,
    refetch: refetchLive
  } = useGetPlantActiveAlarmsQuery({ gateways: liveGateways }, { skip: liveGateways.length === 0 })

  // Match live alarms back to configured `PlantAlarm`s by
  // (gateway_url, tag_path, alarm_name). The Ignition `source` field
  // is `prov:<provider>:/tag:<path>:/alm:<name>`; we re-encode it to
  // our canonical `[<provider>]<path>` form and pair with the alarm
  // name. Same alarm name on different tags or gateways is allowed,
  // so the gateway URL is part of the key.
  const liveByKey = React.useMemo(() => {
    const map = new Map<string, PlantActiveAlarm>()
    for (const live of liveData?.alarms ?? []) {
      const parsed = parseIgnitionAlarmSource(live.source)
      if (!parsed) continue
      const key = liveAlarmKey(live.gateway_url, parsed.tagPath, parsed.alarmName)
      map.set(key, live)
    }
    return map
  }, [liveData])

  const liveStateFor = React.useCallback(
    (alarm: PlantAlarm): PlantActiveAlarm | undefined => {
      if (!alarm.gateway?.gateway_url || !alarm.tag_path) return undefined
      return liveByKey.get(liveAlarmKey(alarm.gateway.gateway_url, alarm.tag_path, alarm.name))
    },
    [liveByKey]
  )

  const togglePriority = React.useCallback((p: PlantAlarmPriority) => {
    setActivePriorities((prev) => {
      const next = new Set(prev)
      if (next.has(p)) next.delete(p)
      else next.add(p)
      return next
    })
  }, [])

  const trimmedSearch = search.trim().toLowerCase()

  // Apply the status + ack filter to the flat alarm list first. The
  // grouped view re-uses this filtered list so its UDT > instance
  // buckets shrink coherently when the user selects "Active Unacked".
  const statusFilteredAlarms = React.useMemo(() => {
    if (statusFilter === 'all') return index.alarms
    return index.alarms.filter((alarm) => {
      const live = liveStateFor(alarm)
      if (!live) return false
      if (!isActiveState(live.state)) return false
      return matchesAckFilter(live.state, ackFilter)
    })
  }, [index.alarms, statusFilter, ackFilter, liveStateFor])

  const filteredFlat = React.useMemo(() => {
    const base = trimmedSearch
      ? statusFilteredAlarms.filter((a) => matchesSearch(a, trimmedSearch))
      : statusFilteredAlarms
    if (!paintOn) return base
    return sortAlarmsPaintedFirst(base, markFor)
  }, [statusFilteredAlarms, trimmedSearch, paintOn, markFor])

  // Group view paginates by section (one UDTDefinition + the
  // "Ungrouped / No UDT" bucket as a single trailing section). Each
  // section can contain many alarms; the per-section instance/alarm
  // expansions are collapsed by default so the page height stays bounded.
  //
  // When a status filter is active we rebuild the groups from the
  // filtered flat list so empty UDTs / instances disappear instead of
  // rendering as zero-row stubs.
  //
  // With session paint on, changed sections (and their instances /
  // leaves) float to the front so paint work is visible without paging.
  const groupedSections = React.useMemo<GroupedSection[]>(() => {
    let sections: GroupedSection[]
    if (statusFilter === 'all') {
      sections = index.groups.map((group) => ({
        kind: 'definition',
        group
      }))
      if (index.ungrouped.length > 0 || orphanAlarmGhosts.length > 0) {
        sections.push({ kind: 'ungrouped', alarms: index.ungrouped })
      }
    } else {
      sections = regroupSections(statusFilteredAlarms)
      if (
        orphanAlarmGhosts.length > 0 &&
        !sections.some((section) => section.kind === 'ungrouped')
      ) {
        sections.push({ kind: 'ungrouped', alarms: [] })
      }
    }
    if (!paintOn) return sections
    return sortSectionsPaintedFirst(sections, markFor, ghostsByContainerId, orphanAlarmGhosts)
  }, [
    statusFilter,
    index.groups,
    index.ungrouped,
    statusFilteredAlarms,
    orphanAlarmGhosts,
    paintOn,
    markFor,
    ghostsByContainerId
  ])

  React.useEffect(() => {
    setPage(0)
  }, [grouped, trimmedSearch, activePriorities, statusFilter, ackFilter])

  const totalItems = showGrouped ? groupedSections.length : filteredFlat.length
  const pageSize = showGrouped ? PAGE_SIZE_GROUPED : PAGE_SIZE_FLAT
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  // Clamp the live page index so an out-of-range page (e.g. data
  // shrunk between renders) never renders an empty slice.
  const safePage = Math.min(page, totalPages - 1)
  const pageStart = safePage * pageSize
  const pageEnd = pageStart + pageSize

  const pagedSections = React.useMemo(
    () => (showGrouped ? groupedSections.slice(pageStart, pageEnd) : []),
    [showGrouped, groupedSections, pageStart, pageEnd]
  )
  const pagedFlat = React.useMemo(
    () => (showGrouped ? [] : filteredFlat.slice(pageStart, pageEnd)),
    [showGrouped, filteredFlat, pageStart, pageEnd]
  )

  // Alarm rows the user can actually see: the leaves under whichever
  // definition / instance sections are expanded on the current page.
  const expandedAlarms = React.useMemo((): ExpandedAlarm[] => {
    const revealed: ExpandedAlarm[] = []
    const push = (alarm: PlantAlarm, udtInstance?: string): void => {
      revealed.push({
        name: alarm.name,
        tagPath: alarm.tag_path,
        priority: alarm.priority,
        liveState: liveStateFor(alarm)?.state,
        udtInstance,
        id: alarm.id
      })
    }
    for (const section of pagedSections) {
      if (section.kind === 'ungrouped') {
        if (!expandedKeys.has(UNGROUPED_SECTION_KEY)) continue
        for (const alarm of section.alarms) push(alarm)
        continue
      }
      if (!expandedKeys.has(definitionSectionKey(section.group.definition.id))) continue
      for (const inst of section.group.instances) {
        if (!expandedKeys.has(instanceSectionKey(inst.instance.id))) continue
        for (const alarm of inst.alarms) push(alarm, inst.instance.name)
      }
    }
    return revealed
  }, [pagedSections, expandedKeys, liveStateFor])

  const liveStatus: LiveStatusMeta = {
    enabled: liveGateways.length > 0,
    gatewayCount: liveGateways.length,
    isFetching: liveFetching,
    isError: liveIsError,
    errors: liveData?.errors ?? {},
    activeCount: liveData?.alarms.filter((a) => isActiveState(a.state)).length ?? 0,
    onRefresh: refetchLive
  }

  useChatAppContext(
    buildAlarmsAppContextEntries({
      grouped: showGrouped,
      statusFilter,
      ackFilter,
      activePriorities: Array.from(activePriorities),
      search: trimmedSearch,
      matchingAlarmCount: filteredFlat.length,
      totalAlarmCount: totalCount,
      expandedAlarms
    })
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-2xl font-semibold text-ink">Alarms</h1>
        <p className="m-0 text-sm text-ink-3">
          {subtitle(index, totalCount, isLoading, liveStatus, statusFilter, filteredFlat.length)}
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-3">
          {/* Priority chips — server-side allow-list. Empty selection = "all". */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-11 font-medium uppercase tracking-[0.06em] text-ink-3">
              Priority
            </span>
            {PLANT_ALARM_PRIORITIES.map((p) => {
              const active = activePriorities.has(p)
              const tier = priorityToTier(p)
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePriority(p)}
                  aria-pressed={active}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${
                    active
                      ? `${TIER_PILL_CLASSES[tier]} border-transparent`
                      : 'border-input bg-card text-ink-3 hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  {p}
                </button>
              )
            })}
            {activePriorities.size > 0 && (
              <button
                type="button"
                onClick={() => setActivePriorities(new Set())}
                className="text-11 text-ink-3 underline-offset-2 hover:text-ink hover:underline"
              >
                Clear
              </button>
            )}
          </div>

          <StatusFilterGroup
            value={statusFilter}
            onChange={setStatusFilter}
            ackValue={ackFilter}
            onAckChange={setAckFilter}
            liveStatus={liveStatus}
          />

          <div className="ml-auto flex items-center gap-2">
            <RefreshLiveButton liveStatus={liveStatus} />
            {grouped && (
              // Search is only meaningful in flat mode (the tree presents
              // groups, not rows). Hide it when grouped so the user knows
              // search lives with the flat view.
              <span className="text-11 text-ink-3">Search in flat view</span>
            )}
            <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-ink-2">
              <span>Grouped</span>
              <Switch checked={grouped} onCheckedChange={setGrouped} aria-label="Grouped view" />
            </label>
          </div>
        </div>

        {!grouped && (
          <div className="mt-1 flex items-center gap-2">
            <SearchIcon size={14} className="text-ink-3" aria-hidden />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by tag path, alarm name, UDT, or instance…"
              className="h-8 max-w-md text-xs"
            />
          </div>
        )}

        {liveStatus.isError && (
          <div className="mt-1 text-11 text-status-critical">
            Couldn't load live alarm status — showing configuration only.
          </div>
        )}
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load alarms"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && totalCount === 0 && orphanAlarmGhosts.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading alarms…</div>
        </div>
      ) : showEmpty ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No alarms"
            hint={
              activePriorities.size > 0
                ? 'No alarms match the selected priorities.'
                : 'Ingest an Ignition backup with alarmed tags to populate this tab.'
            }
            icon={BellRingIcon}
          />
        </div>
      ) : showGrouped ? (
        <>
          <GroupedView
            sections={pagedSections}
            isFetching={isFetching}
            liveStateFor={liveStateFor}
            statusFilter={statusFilter}
            ackFilter={ackFilter}
            orphanAlarmGhosts={orphanAlarmGhosts}
            expandedKeys={expandedKeys}
            onToggleExpanded={toggleExpanded}
          />
          <PaginationFooter
            page={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
            rangeStart={totalItems === 0 ? 0 : pageStart + 1}
            rangeEnd={Math.min(pageEnd, totalItems)}
            totalItems={totalItems}
            itemLabel={totalItems === 1 ? 'section' : 'sections'}
          />
        </>
      ) : (
        <>
          <FlatView
            alarms={pagedFlat}
            searchActive={Boolean(trimmedSearch)}
            liveStateFor={liveStateFor}
            statusFilter={statusFilter}
            ackFilter={ackFilter}
          />
          <PaginationFooter
            page={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
            rangeStart={totalItems === 0 ? 0 : pageStart + 1}
            rangeEnd={Math.min(pageEnd, totalItems)}
            totalItems={totalItems}
            itemLabel={totalItems === 1 ? 'alarm' : 'alarms'}
          />
        </>
      )}
    </div>
  )
}

// ─── Grouped view ─────────────────────────────────────────────

type GroupedSection =
  | { kind: 'definition'; group: PlantAlarmDefinitionGroup }
  | { kind: 'ungrouped'; alarms: PlantAlarm[] }

type LiveStateFor = (alarm: PlantAlarm) => PlantActiveAlarm | undefined

interface ViewCommonProps {
  liveStateFor: LiveStateFor
  statusFilter: PlantAlarmStatusFilter
  ackFilter: PlantAlarmAckFilter
}

/** Page-owned accordion state, threaded down to every collapsible section. */
interface ExpansionProps {
  expandedKeys: ReadonlySet<string>
  onToggleExpanded: (key: string) => void
}

function GroupedView({
  sections,
  isFetching,
  liveStateFor,
  statusFilter,
  ackFilter,
  orphanAlarmGhosts,
  expandedKeys,
  onToggleExpanded
}: {
  sections: GroupedSection[]
  isFetching: boolean
  orphanAlarmGhosts: PlantSessionGhostView[]
} & ViewCommonProps &
  ExpansionProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-card">
      {sections.map((section) =>
        section.kind === 'definition' ? (
          <DefinitionSection
            key={section.group.definition.id}
            group={section.group}
            liveStateFor={liveStateFor}
            statusFilter={statusFilter}
            ackFilter={ackFilter}
            expandedKeys={expandedKeys}
            onToggleExpanded={onToggleExpanded}
          />
        ) : (
          <UngroupedSection
            key="__ungrouped"
            alarms={section.alarms}
            liveStateFor={liveStateFor}
            statusFilter={statusFilter}
            ackFilter={ackFilter}
            orphanAlarmGhosts={orphanAlarmGhosts}
            expandedKeys={expandedKeys}
            onToggleExpanded={onToggleExpanded}
          />
        )
      )}
      {isFetching && <div className="text-xs text-ink-3">Refreshing…</div>}
    </div>
  )
}

function DefinitionSection({
  group,
  liveStateFor,
  statusFilter,
  ackFilter,
  expandedKeys,
  onToggleExpanded
}: {
  group: PlantAlarmDefinitionGroup
} & ViewCommonProps &
  ExpansionProps): React.ReactElement {
  const sectionKey = definitionSectionKey(group.definition.id)
  const open = expandedKeys.has(sectionKey)
  const { active, filter, markFor, ghostsByContainerId } = usePlantSessionPaint()
  const paintOn = active && filter.enabled
  const alarmIds = React.useMemo(
    () => group.instances.flatMap((inst) => inst.alarms.map((alarm) => alarm.id)),
    [group.instances]
  )
  const tagIds = React.useMemo(
    () => group.instances.flatMap((inst) => inst.alarms.map((alarm) => alarm.tag_id)),
    [group.instances]
  )
  const defGhosts = React.useMemo(
    () => collectGhostsForContainers(tagIds, ghostsByContainerId, markFor, 'Alarm'),
    [ghostsByContainerId, markFor, tagIds]
  )
  const instances = React.useMemo(() => {
    if (!paintOn) return group.instances
    return sortInstancesPaintedFirst(group.instances, markFor, ghostsByContainerId)
  }, [group.instances, paintOn, markFor, ghostsByContainerId])
  return (
    <section className="rounded-xl border bg-card text-card-foreground shadow-sm">
      <button
        type="button"
        onClick={() => onToggleExpanded(sectionKey)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 p-3.5 text-left hover:bg-accent hover:text-accent-foreground"
      >
        {open ? (
          <CaretDownIcon size={14} className="text-ink-3" aria-hidden />
        ) : (
          <CaretRightIcon size={14} className="text-ink-3" aria-hidden />
        )}
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
          <BellRingIcon size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">
            {group.definition.name}
          </span>
          <span className="block truncate text-11 text-ink-3">UDT Definition</span>
        </span>
        <SessionDescendantPaintMarks
          ids={alarmIds}
          markFor={markFor}
          hasGhosts={defGhosts.length > 0}
        />
        <Badge variant="secondary">
          {group.alarmCount} {group.alarmCount === 1 ? 'alarm' : 'alarms'}
        </Badge>
        <Badge variant="outline">
          {group.instances.length} {group.instances.length === 1 ? 'instance' : 'instances'}
        </Badge>
      </button>
      {open && (
        <ul className="flex flex-col gap-2 border-t p-2">
          {instances.map((inst) => (
            <InstanceRow
              key={inst.instance.id}
              group={inst}
              liveStateFor={liveStateFor}
              statusFilter={statusFilter}
              ackFilter={ackFilter}
              expandedKeys={expandedKeys}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function InstanceRow({
  group,
  liveStateFor,
  statusFilter,
  ackFilter,
  expandedKeys,
  onToggleExpanded
}: {
  group: PlantAlarmInstanceGroup
} & ViewCommonProps &
  ExpansionProps): React.ReactElement {
  const sectionKey = instanceSectionKey(group.instance.id)
  const open = expandedKeys.has(sectionKey)
  const { active, filter, markFor, ghostsByContainerId } = usePlantSessionPaint()
  const paintOn = active && filter.enabled
  const alarmIds = React.useMemo(() => group.alarms.map((alarm) => alarm.id), [group.alarms])
  const tagIds = React.useMemo(() => group.alarms.map((alarm) => alarm.tag_id), [group.alarms])
  const ghosts = React.useMemo(
    () => collectGhostsForContainers(tagIds, ghostsByContainerId, markFor, 'Alarm'),
    [ghostsByContainerId, markFor, tagIds]
  )
  const alarms = React.useMemo(() => {
    if (!paintOn) return group.alarms
    return sortAlarmsPaintedFirst(group.alarms, markFor)
  }, [group.alarms, paintOn, markFor])
  return (
    <li className="rounded-lg border bg-background">
      <button
        type="button"
        onClick={() => onToggleExpanded(sectionKey)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 p-2 text-left hover:bg-accent hover:text-accent-foreground"
      >
        {open ? (
          <CaretDownIcon size={12} className="text-ink-3" aria-hidden />
        ) : (
          <CaretRightIcon size={12} className="text-ink-3" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{group.instance.name}</span>
          {group.instance.tag_path && (
            <span className="block truncate font-mono text-11 text-ink-3">
              {group.instance.tag_path}
            </span>
          )}
        </span>
        <SessionDescendantPaintMarks
          ids={alarmIds}
          markFor={markFor}
          hasGhosts={ghosts.length > 0}
        />
        <Badge variant="outline">
          {group.alarms.length} {group.alarms.length === 1 ? 'alarm' : 'alarms'}
        </Badge>
      </button>
      {open && (
        <ul className="flex flex-col gap-1 border-t p-2">
          {ghosts.map((ghost) => (
            <li key={ghost.id}>
              <SessionGhostRow name={ghost.name ?? ghost.id} view={ghost.view} />
            </li>
          ))}
          {alarms.map((alarm) => (
            <AlarmLeaf
              key={alarm.id}
              alarm={alarm}
              liveStateFor={liveStateFor}
              statusFilter={statusFilter}
              ackFilter={ackFilter}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function AlarmLeaf({
  alarm,
  liveStateFor,
  statusFilter
}: { alarm: PlantAlarm } & ViewCommonProps): React.ReactElement {
  const live = liveStateFor(alarm)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(alarm.id)
  return (
    <li
      className={sessionMarkClass(
        'flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <AlarmPriorityChip priority={alarm.priority} />
      <LiveStateChip live={live} alarm={alarm} statusFilter={statusFilter} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink">{alarm.name}</span>
        {alarm.tag_path && (
          <span className="block truncate font-mono text-11 text-ink-3">{alarm.tag_path}</span>
        )}
      </span>
      <SessionRowEndMark mark={mark} />
    </li>
  )
}

function UngroupedSection({
  alarms,
  liveStateFor,
  statusFilter,
  ackFilter,
  orphanAlarmGhosts,
  expandedKeys,
  onToggleExpanded
}: {
  alarms: PlantAlarm[]
  orphanAlarmGhosts: PlantSessionGhostView[]
} & ViewCommonProps &
  ExpansionProps): React.ReactElement {
  const open = expandedKeys.has(UNGROUPED_SECTION_KEY)
  const { active, filter, markFor, ghostsByContainerId } = usePlantSessionPaint()
  const paintOn = active && filter.enabled
  const alarmIds = React.useMemo(() => alarms.map((alarm) => alarm.id), [alarms])
  const tagIds = React.useMemo(() => alarms.map((alarm) => alarm.tag_id), [alarms])
  const placedGhosts = React.useMemo(
    () => collectGhostsForContainers(tagIds, ghostsByContainerId, markFor, 'Alarm'),
    [ghostsByContainerId, markFor, tagIds]
  )
  const ghosts = React.useMemo(() => {
    const seen = new Set(placedGhosts.map((ghost) => ghost.id))
    const merged = [...placedGhosts]
    for (const ghost of orphanAlarmGhosts) {
      if (seen.has(ghost.id)) continue
      seen.add(ghost.id)
      merged.push(ghost)
    }
    return merged
  }, [orphanAlarmGhosts, placedGhosts])
  const total = alarms.length + ghosts.length
  const sortedAlarms = React.useMemo(() => {
    if (!paintOn) return alarms
    return sortAlarmsPaintedFirst(alarms, markFor)
  }, [alarms, paintOn, markFor])
  return (
    <section className="rounded-xl border bg-card text-card-foreground shadow-sm">
      <button
        type="button"
        onClick={() => onToggleExpanded(UNGROUPED_SECTION_KEY)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 p-3.5 text-left hover:bg-accent hover:text-accent-foreground"
      >
        {open ? (
          <CaretDownIcon size={14} className="text-ink-3" aria-hidden />
        ) : (
          <CaretRightIcon size={14} className="text-ink-3" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">Ungrouped / No UDT</span>
          <span className="block truncate text-11 text-ink-3">
            Alarms on standalone tags (not derived from a UDT instance)
          </span>
        </span>
        <SessionDescendantPaintMarks
          ids={alarmIds}
          markFor={markFor}
          hasGhosts={ghosts.length > 0}
        />
        <Badge variant="secondary">
          {total} {total === 1 ? 'alarm' : 'alarms'}
        </Badge>
      </button>
      {open && (
        <ul className="flex flex-col gap-1 border-t p-2">
          {ghosts.map((ghost) => (
            <li key={ghost.id}>
              <SessionGhostRow name={ghost.name ?? ghost.id} view={ghost.view} />
            </li>
          ))}
          {sortedAlarms.map((alarm) => (
            <AlarmLeaf
              key={alarm.id}
              alarm={alarm}
              liveStateFor={liveStateFor}
              statusFilter={statusFilter}
              ackFilter={ackFilter}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

// ─── Flat view ────────────────────────────────────────────────

function FlatView({
  alarms,
  searchActive,
  liveStateFor,
  statusFilter
}: {
  alarms: PlantAlarm[]
  searchActive: boolean
} & ViewCommonProps): React.ReactElement {
  if (alarms.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <Empty
          title="No alarms match"
          hint={
            searchActive
              ? 'Clear or refine your search to see more rows.'
              : statusFilter === 'active'
                ? 'No alarms are currently active. Switch to "All" to see configured alarms.'
                : undefined
          }
          icon={BellRingIcon}
        />
      </div>
    )
  }
  return (
    <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-11 uppercase tracking-[0.06em] text-ink-3">
            <th className="px-3 py-2 font-medium">Priority</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Alarm</th>
            <th className="px-3 py-2 font-medium">Tag path</th>
            <th className="px-3 py-2 font-medium">UDT</th>
          </tr>
        </thead>
        <tbody>
          {alarms.map((alarm) => (
            <FlatAlarmRow
              key={alarm.id}
              alarm={alarm}
              liveStateFor={liveStateFor}
              statusFilter={statusFilter}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FlatAlarmRow({
  alarm,
  liveStateFor,
  statusFilter
}: {
  alarm: PlantAlarm
  liveStateFor: LiveStateFor
  statusFilter: PlantAlarmStatusFilter
}): React.ReactElement {
  const live = liveStateFor(alarm)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(alarm.id)
  return (
    <tr
      className={sessionMarkClass(
        'border-b last:border-b-0 hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <td className="px-3 py-2">
        <AlarmPriorityChip priority={alarm.priority} />
      </td>
      <td className="px-3 py-2">
        <LiveStateChip live={live} alarm={alarm} statusFilter={statusFilter} />
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <div className="text-ink">{alarm.name}</div>
            {alarm.label && <div className="truncate text-11 text-ink-3">{alarm.label}</div>}
          </div>
          <SessionRowEndMark mark={mark} />
        </div>
      </td>
      <td className="px-3 py-2 align-top font-mono text-11 text-ink-2">
        {alarm.tag_path ?? alarm.tag_name ?? alarm.tag_id}
      </td>
      <td className="px-3 py-2 align-top">
        {alarm.definition ? (
          <div className="text-ink-2">{alarm.definition.name}</div>
        ) : (
          <span className="text-ink-3">—</span>
        )}
        {alarm.instance && <div className="truncate text-11 text-ink-3">{alarm.instance.name}</div>}
      </td>
    </tr>
  )
}

// ─── Shared primitives ────────────────────────────────────────

function AlarmPriorityChip({ priority }: { priority: PlantAlarmPriority }): React.ReactElement {
  const tier = priorityToTier(priority)
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-11 font-medium ${TIER_PILL_CLASSES[tier]}`}
    >
      {priority}
    </span>
  )
}

/**
 * Color-coded chip for the live alarm state — uses the same four-tier
 * severity palette as the priority chip so the page reads consistently:
 *
 *   - `ActiveUnacked` → critical (red)
 *   - `ActiveAcked`   → elevated (amber)
 *   - `Clear*`        → watch (blue) — gateway still reports it but
 *                       the underlying condition has cleared
 *
 * When there's no live row we either hide the chip (status='all', so
 * we don't add visual noise to alarms we don't have telemetry for) or
 * render an `Inactive` / `Unknown` placeholder when the user has
 * actively narrowed to live alarms.
 */
function LiveStateChip({
  live,
  alarm,
  statusFilter
}: {
  live: PlantActiveAlarm | undefined
  alarm: PlantAlarm
  statusFilter: PlantAlarmStatusFilter
}): React.ReactElement | null {
  if (!live) {
    if (statusFilter === 'all') {
      // Render a soft placeholder so the column doesn't jump width and
      // the user can still tell at a glance which alarms have no live
      // surface at all.
      if (!alarm.gateway?.gateway_url) {
        return (
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-11 font-medium text-ink-3">
            No gateway
          </span>
        )
      }
      return (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-11 font-medium text-ink-3">
          Inactive
        </span>
      )
    }
    return null
  }
  const tier = liveStateToTier(live.state)
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-11 font-medium ${TIER_PILL_CLASSES[tier]}`}
      title={describeLiveState(live)}
    >
      {formatLiveState(live.state)}
    </span>
  )
}

function liveStateToTier(state: PlantAlarmLiveState): SeverityTier {
  switch (state) {
    case 'ActiveUnacked':
      return 'critical'
    case 'ActiveAcked':
      return 'elevated'
    case 'ClearUnacked':
    case 'ClearAcked':
      return 'watch'
  }
}

function formatLiveState(state: PlantAlarmLiveState): string {
  switch (state) {
    case 'ActiveUnacked':
      return 'Active · Unacked'
    case 'ActiveAcked':
      return 'Active · Acked'
    case 'ClearUnacked':
      return 'Cleared · Unacked'
    case 'ClearAcked':
      return 'Cleared · Acked'
  }
}

function describeLiveState(live: PlantActiveAlarm): string {
  const segments: string[] = [formatLiveState(live.state)]
  if (typeof live.eventTime === 'number') {
    segments.push(`Event ${new Date(live.eventTime).toLocaleString()}`)
  }
  if (typeof live.ackTime === 'number') {
    segments.push(`Ack ${new Date(live.ackTime).toLocaleString()}`)
  }
  if (typeof live.clearedTime === 'number') {
    segments.push(`Cleared ${new Date(live.clearedTime).toLocaleString()}`)
  }
  return segments.join(' · ')
}

function isActiveState(state: PlantAlarmLiveState): boolean {
  return state === 'ActiveUnacked' || state === 'ActiveAcked'
}

function isUnackedState(state: PlantAlarmLiveState): boolean {
  return state === 'ActiveUnacked' || state === 'ClearUnacked'
}

function matchesAckFilter(state: PlantAlarmLiveState, filter: PlantAlarmAckFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'unacked':
      return isUnackedState(state)
    case 'acked':
      return !isUnackedState(state)
  }
}

// ─── Live status filter / refresh / source parsing ─────────────

interface LiveStatusMeta {
  enabled: boolean
  gatewayCount: number
  isFetching: boolean
  isError: boolean
  errors: Record<string, string>
  activeCount: number
  onRefresh: () => void
}

function StatusFilterGroup({
  value,
  onChange,
  ackValue,
  onAckChange,
  liveStatus
}: {
  value: PlantAlarmStatusFilter
  onChange: (next: PlantAlarmStatusFilter) => void
  ackValue: PlantAlarmAckFilter
  onAckChange: (next: PlantAlarmAckFilter) => void
  liveStatus: LiveStatusMeta
}): React.ReactElement {
  const liveLabel = liveStatus.enabled ? `Active (${liveStatus.activeCount})` : 'Active'
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-11 font-medium uppercase tracking-[0.06em] text-ink-3">Status</span>
      <SegmentedButton
        items={[
          { value: 'all', label: 'All' },
          { value: 'active', label: liveLabel, disabled: !liveStatus.enabled }
        ]}
        value={value}
        onChange={onChange}
      />
      {value === 'active' && liveStatus.enabled && (
        <>
          <span className="text-11 font-medium uppercase tracking-[0.06em] text-ink-3">Ack</span>
          <SegmentedButton
            items={[
              { value: 'all', label: 'All' },
              { value: 'unacked', label: 'Unacked' },
              { value: 'acked', label: 'Acked' }
            ]}
            value={ackValue}
            onChange={onAckChange}
          />
        </>
      )}
    </div>
  )
}

function SegmentedButton<T extends string>({
  items,
  value,
  onChange
}: {
  items: Array<{ value: T; label: string; disabled?: boolean }>
  value: T
  onChange: (next: T) => void
}): React.ReactElement {
  return (
    <div
      role="radiogroup"
      className="inline-flex items-center rounded-full border border-input bg-card p-0.5 text-xs"
    >
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.value)}
            className={`rounded-full px-2.5 py-0.5 font-medium transition-colors ${
              active
                ? 'bg-brand text-brand-foreground'
                : 'text-ink-3 hover:bg-accent hover:text-accent-foreground'
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

function RefreshLiveButton({ liveStatus }: { liveStatus: LiveStatusMeta }): React.ReactElement {
  const disabled = !liveStatus.enabled || liveStatus.isFetching
  return (
    <button
      type="button"
      onClick={liveStatus.onRefresh}
      disabled={disabled}
      aria-label="Refresh live alarm status"
      title={
        liveStatus.enabled
          ? `Refresh live status across ${liveStatus.gatewayCount} ${liveStatus.gatewayCount === 1 ? 'gateway' : 'gateways'}`
          : 'No gateways resolved — live status unavailable'
      }
      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-xs text-ink-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <RefreshIcon
        size={14}
        className={liveStatus.isFetching ? 'animate-spin' : undefined}
        aria-hidden
      />
      <span>Refresh live</span>
    </button>
  )
}

/**
 * Parse an Ignition alarm source string into its provider / tag-path /
 * alarm-name parts. Format is `prov:<provider>:/tag:<path>:/alm:<name>`.
 * Returns the canonical `[<provider>]<path>` form to match the
 * ontology's `Alarm.tag_path` projection so live rows can be looked up
 * by `(gateway_url, tag_path, alarm_name)`.
 *
 * Tag paths can themselves contain colons in folder names (rare but
 * legal), so we split on the literal `:/tag:` and `:/alm:` delimiters
 * rather than a strict regex.
 */
function parseIgnitionAlarmSource(
  source: string
): { provider: string; tagPath: string; alarmName: string } | null {
  if (!source.startsWith('prov:')) return null
  const tagSplitIdx = source.indexOf(':/tag:')
  if (tagSplitIdx < 0) return null
  const provider = source.slice('prov:'.length, tagSplitIdx)
  const afterTag = source.slice(tagSplitIdx + ':/tag:'.length)
  const alarmSplitIdx = afterTag.indexOf(':/alm:')
  if (alarmSplitIdx < 0) return null
  const path = afterTag.slice(0, alarmSplitIdx)
  const alarmName = afterTag.slice(alarmSplitIdx + ':/alm:'.length)
  if (!provider || !path || !alarmName) return null
  return { provider, tagPath: `[${provider}]${path}`, alarmName }
}

function liveAlarmKey(gatewayUrl: string, tagPath: string, alarmName: string): string {
  return `${gatewayUrl}::${tagPath}::${alarmName}`
}

/**
 * Collect the unique `(gateway_url, project)` set from a list of
 * configured alarms so the live fan-out only hits each gateway once.
 * Alarms with no resolved gateway are silently skipped — they
 * contribute nothing to the live query.
 */
function collectGateways(alarms: PlantAlarm[]): PlantActiveAlarmsGateway[] {
  const seen = new Set<string>()
  const out: PlantActiveAlarmsGateway[] = []
  for (const alarm of alarms) {
    const url = alarm.gateway?.gateway_url
    if (!url) continue
    const project = alarm.gateway?.project
    const key = `${url}::${project ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ gateway_url: url, project })
  }
  return out
}

type AlarmMarkFor = (id: string) => PlantSessionMarkView | undefined

/** Prefer added, then deleted, then updated, then unchanged (stable within rank). */
function paintRank(mark: PlantSessionMarkView | undefined, hasGhosts: boolean): number {
  if (mark === 'added') return 0
  if (mark === 'deleted' || hasGhosts) return 1
  if (mark === 'touched') return 2
  return 3
}

function comparePaintRank(aRank: number, bRank: number, aIndex: number, bIndex: number): number {
  if (aRank !== bRank) return aRank - bRank
  return aIndex - bIndex
}

function sortAlarmsPaintedFirst(
  alarms: readonly PlantAlarm[],
  markFor: AlarmMarkFor
): PlantAlarm[] {
  return alarms
    .map((alarm, index) => ({
      alarm,
      index,
      rank: paintRank(markFor(alarm.id), false)
    }))
    .sort((a, b) => comparePaintRank(a.rank, b.rank, a.index, b.index))
    .map((entry) => entry.alarm)
}

function instancePaintRank(
  group: PlantAlarmInstanceGroup,
  markFor: AlarmMarkFor,
  ghostsByContainerId: ReadonlyMap<string, readonly { labels: string[] }[]>
): number {
  let best = paintRank(markFor(group.instance.id), false)
  let hasGhosts = false
  for (const alarm of group.alarms) {
    best = Math.min(best, paintRank(markFor(alarm.id), false))
    for (const ghost of ghostsByContainerId.get(alarm.tag_id) ?? []) {
      if (ghost.labels.includes('Alarm')) {
        hasGhosts = true
        break
      }
    }
  }
  if (hasGhosts) best = Math.min(best, paintRank(undefined, true))
  return best
}

function sortInstancesPaintedFirst(
  instances: readonly PlantAlarmInstanceGroup[],
  markFor: AlarmMarkFor,
  ghostsByContainerId: ReadonlyMap<string, readonly { labels: string[] }[]>
): PlantAlarmInstanceGroup[] {
  return instances
    .map((group, index) => ({
      group,
      index,
      rank: instancePaintRank(group, markFor, ghostsByContainerId)
    }))
    .sort((a, b) => comparePaintRank(a.rank, b.rank, a.index, b.index))
    .map((entry) => entry.group)
}

function sectionPaintRank(
  section: GroupedSection,
  markFor: AlarmMarkFor,
  ghostsByContainerId: ReadonlyMap<string, readonly { labels: string[] }[]>,
  orphanAlarmGhosts: readonly PlantSessionGhostView[]
): number {
  if (section.kind === 'ungrouped') {
    let best = orphanAlarmGhosts.length > 0 ? paintRank(undefined, true) : 3
    for (const alarm of section.alarms) {
      best = Math.min(best, paintRank(markFor(alarm.id), false))
      for (const ghost of ghostsByContainerId.get(alarm.tag_id) ?? []) {
        if (ghost.labels.includes('Alarm')) {
          best = Math.min(best, paintRank(undefined, true))
          break
        }
      }
    }
    return best
  }
  let best = paintRank(markFor(section.group.definition.id), false)
  for (const inst of section.group.instances) {
    best = Math.min(best, instancePaintRank(inst, markFor, ghostsByContainerId))
  }
  return best
}

function sortSectionsPaintedFirst(
  sections: readonly GroupedSection[],
  markFor: AlarmMarkFor,
  ghostsByContainerId: ReadonlyMap<string, readonly { labels: string[] }[]>,
  orphanAlarmGhosts: readonly PlantSessionGhostView[]
): GroupedSection[] {
  return sections
    .map((section, index) => ({
      section,
      index,
      rank: sectionPaintRank(section, markFor, ghostsByContainerId, orphanAlarmGhosts)
    }))
    .sort((a, b) => comparePaintRank(a.rank, b.rank, a.index, b.index))
    .map((entry) => entry.section)
}

/**
 * Rebuild the grouped sections from a filtered alarm list. Used when
 * the status filter is active so the UDT > instance buckets collapse
 * to only the alarms that actually pass the live filter, instead of
 * rendering empty instances.
 */
function regroupSections(alarms: PlantAlarm[]): GroupedSection[] {
  const groupsByDefId = new Map<string, PlantAlarmDefinitionGroup>()
  const ungrouped: PlantAlarm[] = []
  for (const alarm of alarms) {
    if (!alarm.definition) {
      ungrouped.push(alarm)
      continue
    }
    const defId = alarm.definition.id
    let group = groupsByDefId.get(defId)
    if (!group) {
      group = { definition: alarm.definition, instances: [], alarmCount: 0 }
      groupsByDefId.set(defId, group)
    }
    const instKey = alarm.instance?.id ?? `__no_instance__:${alarm.tag_id}`
    let instGroup = group.instances.find((g) => g.instance.id === instKey)
    if (!instGroup) {
      instGroup = {
        instance: alarm.instance ?? {
          id: instKey,
          name: alarm.tag_name ?? '(no instance)',
          tag_path: alarm.tag_path
        },
        alarms: []
      }
      group.instances.push(instGroup)
    }
    instGroup.alarms.push(alarm)
    group.alarmCount += 1
  }
  const sections: GroupedSection[] = Array.from(groupsByDefId.values()).map((group) => ({
    kind: 'definition',
    group
  }))
  if (ungrouped.length > 0) {
    sections.push({ kind: 'ungrouped', alarms: ungrouped })
  }
  return sections
}

/**
 * Map the five-level Ignition priority scale to the four-tier severity
 * palette already used by `PriorityChip` / prediction pills, so alarm
 * chips speak the same color language as the rest of the app. Both
 * `Diagnostic` and `Low` collapse to the `healthy` tier.
 */
function priorityToTier(priority: PlantAlarmPriority): SeverityTier {
  switch (priority) {
    case 'Critical':
      return 'critical'
    case 'High':
      return 'elevated'
    case 'Medium':
      return 'watch'
    case 'Low':
    case 'Diagnostic':
      return 'healthy'
  }
}

function matchesSearch(alarm: PlantAlarm, q: string): boolean {
  return (
    alarm.name.toLowerCase().includes(q) ||
    (alarm.tag_path ?? '').toLowerCase().includes(q) ||
    (alarm.tag_name ?? '').toLowerCase().includes(q) ||
    (alarm.label ?? '').toLowerCase().includes(q) ||
    (alarm.definition?.name ?? '').toLowerCase().includes(q) ||
    (alarm.instance?.name ?? '').toLowerCase().includes(q)
  )
}

function subtitle(
  index: PlantAlarmsIndex,
  totalCount: number,
  isLoading: boolean,
  liveStatus: LiveStatusMeta,
  statusFilter: PlantAlarmStatusFilter,
  visibleCount: number
): string {
  if (isLoading && totalCount === 0) {
    return 'Loading alarm configurations from the ontology…'
  }
  if (totalCount === 0) {
    return 'Tag alarms configured in the ontology — empty for now.'
  }
  const groupCount = index.groups.length
  const ungroupedCount = index.ungrouped.length
  const segments: string[] = []
  if (statusFilter === 'active') {
    segments.push(`${visibleCount} of ${totalCount} active`)
  } else {
    segments.push(`${totalCount} ${totalCount === 1 ? 'alarm' : 'alarms'}`)
  }
  if (groupCount > 0) {
    segments.push(`${groupCount} ${groupCount === 1 ? 'UDT' : 'UDTs'}`)
  }
  if (ungroupedCount > 0) {
    segments.push(`${ungroupedCount} standalone`)
  }
  if (liveStatus.enabled) {
    segments.push(
      `${liveStatus.activeCount} live · ${liveStatus.gatewayCount} ${liveStatus.gatewayCount === 1 ? 'gateway' : 'gateways'}`
    )
  }
  return `${segments.join(' · ')}.`
}

// ─── Pagination ───────────────────────────────────────────────

/** Rows per page in the flat alarm table. */
const PAGE_SIZE_FLAT = 50
/**
 * Sections per page in the grouped tree. A "section" is one
 * UDTDefinition or the single "Ungrouped / No UDT" bucket — never an
 * individual alarm — because each section can already expand into many
 * instances and alarms on demand. Keeping the count low here means the
 * page never grows taller than the user expects when every section is
 * collapsed.
 */
const PAGE_SIZE_GROUPED = 10

function PaginationFooter({
  page,
  totalPages,
  onPageChange,
  rangeStart,
  rangeEnd,
  totalItems,
  itemLabel
}: {
  page: number
  totalPages: number
  onPageChange: (next: number) => void
  rangeStart: number
  rangeEnd: number
  totalItems: number
  itemLabel: string
}): React.ReactElement | null {
  if (totalItems === 0 || totalPages <= 1) return null

  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, next))
    if (clamped !== page) onPageChange(clamped)
  }

  const canPrev = page > 0
  const canNext = page < totalPages - 1

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-3 py-2 text-xs text-ink-3 shadow-sm">
      <span>
        Showing <span className="font-medium text-ink-2">{rangeStart.toLocaleString()}</span>
        {'–'}
        <span className="font-medium text-ink-2">{rangeEnd.toLocaleString()}</span> of{' '}
        <span className="font-medium text-ink-2">{totalItems.toLocaleString()}</span> {itemLabel}
      </span>
      <div className="flex items-center gap-1">
        <PaginationButton
          onClick={() => goTo(0)}
          disabled={!canPrev}
          label="First page"
          icon={CaretFirstIcon}
        />
        <PaginationButton
          onClick={() => goTo(page - 1)}
          disabled={!canPrev}
          label="Previous page"
          icon={CaretLeftIcon}
        />
        <span className="px-2 text-xs text-ink-2">
          Page <span className="font-medium text-ink">{page + 1}</span> of{' '}
          <span className="font-medium text-ink">{totalPages}</span>
        </span>
        <PaginationButton
          onClick={() => goTo(page + 1)}
          disabled={!canNext}
          label="Next page"
          icon={CaretRightIcon}
        />
        <PaginationButton
          onClick={() => goTo(totalPages - 1)}
          disabled={!canNext}
          label="Last page"
          icon={CaretLastIcon}
        />
      </div>
    </div>
  )
}

function PaginationButton({
  onClick,
  disabled,
  label,
  icon: Icon
}: {
  onClick: () => void
  disabled: boolean
  label: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex size-7 items-center justify-center rounded-md border border-input bg-card text-ink-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card disabled:hover:text-ink-3"
    >
      <Icon size={14} />
    </button>
  )
}

const EMPTY_INDEX: PlantAlarmsIndex = { alarms: [], groups: [], ungrouped: [] }

export default AlarmsPage
